import { useCallback, useMemo } from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';
import { LaunchWorkspaceQuizDialogActionSetter } from '~teacher/launchv2/atoms';
import { DispatchConfirmStopRunningActivityAction } from '~teacher/launchv2/ConfirmLaunchQuiz';

interface UseLaunchQuizActionParams {
  dispatchConfirmStopRunningActivity: DispatchConfirmStopRunningActivityAction;
  setLaunchWorkspaceQuizIsOpen: LaunchWorkspaceQuizDialogActionSetter;
  activityIsLive: boolean;
  workspaceId: string;
  paywallCallback: () => void;
}

export function useLaunchQuizAction({
  dispatchConfirmStopRunningActivity,
  setLaunchWorkspaceQuizIsOpen,
  activityIsLive,
  workspaceId,
  paywallCallback,
}: UseLaunchQuizActionParams) {
  const {
    permissions: { launchWorkspaceQuiz: canLaunchWorkspaceQuiz },
  } = useWorkspacePermissionsV2({ workspaceId });

  const openLaunchDialog = useCallback(
    ({
      workspaceQuizId,
      workspaceId,
      quizName,
    }: {
      workspaceQuizId: string;
      workspaceId: string;
      quizName: string;
    }) => {
      setLaunchWorkspaceQuizIsOpen({
        type: 'open',
        payload: {
          workspaceQuizId: workspaceQuizId,
          workspaceId: workspaceId,
          quizName: quizName,
        },
      });
    },
    [setLaunchWorkspaceQuizIsOpen]
  );

  return useMemo(() => {
    return {
      key: 'launch',
      title: translate('Launch'),
      action: ({ data, close }) => {
        const { workspaceId, id: workspaceQuizId, name: quizName } = data[0];

        if (!activityIsLive) {
          openLaunchDialog({ workspaceQuizId, workspaceId, quizName });
        } else {
          dispatchConfirmStopRunningActivity({
            type: 'open',
            payload: {
              openLaunchQuizDialog: () => {
                dispatchConfirmStopRunningActivity({ type: 'close' });
                openLaunchDialog({ workspaceQuizId, workspaceId, quizName });
              },
              onClose: () =>
                dispatchConfirmStopRunningActivity({ type: 'close' }),
            },
          });
        }
        close();
      },
      constraint: ({ data: quizzes }) => {
        return quizzes.every((wi) => wi.type === 'quiz');
      },
      showProBadge: !canLaunchWorkspaceQuiz,
      disabled: ({ data }) => !canLaunchWorkspaceQuiz || data.length !== 1,
      paywallCallback: paywallCallback,
    } as Action<WorkspaceItem>;
  }, [
    paywallCallback,
    activityIsLive,
    openLaunchDialog,
    dispatchConfirmStopRunningActivity,
    canLaunchWorkspaceQuiz,
  ]);
}
